import ExcelJS from "exceljs";
import { dataImg } from "../../../../../utils/dataImg";
import moment from "moment";
import formatIndianNumber from "../../../../../utils/formatIndianNumber";
import getProposalAvailableFromLabel from "../../../../../utils/getProposalAvailableFromLabel";
import listBlockedInventories from "../../../../../utils/listBlockedInventories";
import { saveAs } from "file-saver";
import convertS3ToCloudFront from "../../../../../utils/convertS3ToCloudFront";
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const getExcelColumnLetter = (colNumber) => {
  let letter = "";
  while (colNumber > 0) {
    const temp = (colNumber - 1) % 26;
    letter = String.fromCharCode(65 + temp) + letter;
    colNumber = Math.floor((colNumber - temp - 1) / 26);
  }
  return letter;
};
const normalizeHeader = (s) => String(s || "").replace(/\s+/g, " ").trim();
const formatIndianAllNumbers = (val) => {
  if (val === null || val === undefined) return val;
  if (typeof val !== "number" || !Number.isFinite(val)) return val;

  try {
    // if your util supports decimals well, it will work
    return String(formatIndianNumber(val));
  } catch {
    // fallback: Indian grouping + keep up to 6 decimals
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 6 }).format(val);
  }
};

// Excel numeric cells: Sr.no, W, H, Sq Ft must stay as numbers (not Indian-formatted strings)
const asExcelNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const asSqFt = (inv, isKiosk) => {
  const w = asExcelNumber(inv?.invId?.width);
  const h = asExcelNumber(inv?.invId?.height);
  if (w == null || h == null) return null;
  if (isKiosk) {
    const u = asExcelNumber(inv?.invId?.unit);
    if (u == null) return null;
    return w * h * u * 2;
  }
  return w * h;
};

// Excel number format: comma as thousands separator, no decimal places
const COMMA_NUMBER_FMT = "#,##0";

const ratePerMonthNumeric = (inv, isKiosk) => {
  if (isKiosk && inv?.invId?.ratePerMonth != null && inv?.invId?.unit != null) {
    const rpm = Number(inv.invId.ratePerMonth);
    const unit = Number(inv.invId.unit);
    if (Number.isFinite(rpm) && Number.isFinite(unit) && unit !== 0) {
      const n = rpm / unit;
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }
  return asExcelNumber(inv?.invId?.ratePerMonth);
};

/**
 * Apply comma number format (#,##0) to money columns when cell is numeric:
 * Rate per month, Special rate per month, Printing/Mounting/Innovation charges
 */
const applyCommaNumFmtToDataRow = (excelRow, headers) => {
  (headers || []).forEach((h, i) => {
    const col = i + 1;
    const hNorm = normalizeHeader(h).toLowerCase();
    const isRatePerMonth =
      hNorm.includes("rate per") && hNorm.includes("month");
    const isPrint =
      hNorm.includes("printing") && hNorm.includes("charg");
    const isMount =
      hNorm.includes("mounting") && hNorm.includes("charg");
    const isInnovation =
      hNorm.includes("innovation") && hNorm.includes("charg");
    if (!isRatePerMonth && !isPrint && !isMount && !isInnovation) return;
    const cell = excelRow.getCell(col);
    if (typeof cell.value === "number" && Number.isFinite(cell.value)) {
      cell.numFmt = COMMA_NUMBER_FMT;
    }
  });
};

const handleDownloadExcel = async (
  clientName,
  proposalInventories,
  packageRate,
  proposalMainData,
  isEmail = false,
  setShowLoader,
  setExcelURL
) => {
  let isGenerateLink = false;
  console.log("generate link", isGenerateLink);
  console.log("proposalInventories: ", proposalInventories);
  if (!isGenerateLink) {
    setShowLoader(true);
  }
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Proposal Data");

  // Add the header image
  const imageId = workbook.addImage({
    base64: dataImg,
    extension: "jpeg",
  });

  worksheet.getCell("A3").value = "To";
  worksheet.getCell("A4").value = "Client:";
  worksheet.getCell("A5").value = "Date:";

  // Header values in column B
  worksheet.getCell("B3").value = "";
  worksheet.getCell("B4").value = clientName || "";
  worksheet.getCell("B5").value = moment().format("DD-MM-YYYY");

  // Styling
  ["A3", "A4", "A5"].forEach(c => {
    worksheet.getCell(c).font = { bold: true, size: 11 };
    worksheet.getCell(c).alignment = { horizontal: "left", vertical: "middle" };
  });

  ["B3", "B4", "B5"].forEach(c => {
    worksheet.getCell(c).font = { bold: true, size: 11 };
    worksheet.getCell(c).alignment = { horizontal: "left", vertical: "middle" };
  });
  worksheet.getColumn("A").width = 14;
  // Display Cost and Production Details Headers
  let displayCostHeader;
  const hasSpecialRateChanged = proposalInventories.some(
    (ele) => ele.specialRate !== ele.invId.ratePerMonth
  );
  const isThereInnovationRate = proposalInventories.some(
    (ele) => ele.innovationRate
  );

  const isAllKiosk = proposalInventories.every(
    (ele) => ele.invId.typeOfMedia.name === "Kiosk"
  );

  // Choose the title dynamically
  const pmChargeTitle = isAllKiosk
    ? "P & M Charges / Pole"
    : "Printing & Mounting Charges";

  const ratePerMonthTitle = isAllKiosk
    ? "Rate Per Month / Pole"
    : "Rate Per Month";

  if (packageRate) {
    displayCostHeader = [
      "Sr.No",
      "Location",
      "Media Type",
      "Size (W)",
      "Size (H)",
      ...(isAllKiosk ? ["Unit"] : []),
      "Sq Ft",
      "Illu",
      "Rate Per Month",
      "Package Rate",
      //  pmChargeTitle,
      "Printing & Mounting Charges",
      "Available From",
      "Lat Long with Hyperlink",

    ];
  } else {
    if (!isThereInnovationRate) {
      if (hasSpecialRateChanged) {
        displayCostHeader = [
          "Sr.No",
          "Location",
          "Media Type",
          "Size (W)",
          "Size (H)",
          ...(isAllKiosk ? ["Unit"] : []),
          "Sq Ft",
          "Illu",
          "Rate Per\n Month",
          "Special Rate \nPer Month",
          //  pmChargeTitle,
          "Printing\n Charges",
          "Mounting\n Charges",
          "Available From",
          "Lat Long\n with Hyperlink",

        ];
      } else {
        displayCostHeader = [
          "Sr.No",
          "Location",
          "Media Type",
          "Size (W)",
          "Size (H)",
          ...(isAllKiosk ? ["Unit"] : []),
          "Sq Ft",
          "Illu",
          "Rate Per\n Month",
          //  pmChargeTitle,
          "Printing\n Charges",
          "Mounting \nCharges",
          "Available From",
          "Lat Long\n with Hyperlink",

        ];
      }
    } else {
      if (hasSpecialRateChanged) {
        displayCostHeader = [
          "Sr.No",
          "Location",
          "Media Type",
          "Size (W)",
          "Size (H)",
          ...(isAllKiosk ? ["Unit"] : []),
          "Sq Ft",
          "Illu",
          "Rate Per\n Month",
          "Special Rate \nPer Month",
          //  pmChargeTitle,
          "Printing\n Charges",
          "Mounting\n Charges",
          "Innovation\n Charges",
          "Available From",
          "Lat Long\n with Hyperlink",

        ];
      } else {
        displayCostHeader = [
          "Sr.No",
          "Location",
          "Media Type",
          "Size (W)",
          "Size (H)",
          ...(isAllKiosk ? ["Unit"] : []),
          "Sq Ft",
          "Illu",
          "Rate Per\n Month",
          //  pmChargeTitle,
          "Printing\n Charges",
          "Mounting\n Charges",
          "Innovation\n Charges",
          "Available From",
          "Lat Long\n with Hyperlink",
        ];
      }
    }
  }
  const lastColIndex = displayCostHeader.length;
  const lastColLetter = getExcelColumnLetter(lastColIndex);
  const logoRange = `${lastColLetter}2:${lastColLetter}5`;
  worksheet.addImage(imageId, logoRange);
  worksheet.mergeCells(logoRange);


  const wrapHeaders = [
    "Rate Per Month",
    "Special Rate Per Month",
    "Printing Charges",
    "Mounting Charges",
    "Lat Long with Hyperlink",
  ].map(normalizeHeader);
  const headerRow = worksheet.addRow(displayCostHeader);
  headerRow.font = { bold: true };

  // Apply alignment, border, and background color
  displayCostHeader.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);

    const shouldWrap = wrapHeaders.some(key =>
      header.replace(/\n/g, " ").includes(key)
    );
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };

    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFF00" },
    };

    cell.alignment = {
      wrapText: shouldWrap,
      horizontal: "center",
      vertical: "middle",
    };
  });

  headerRow.height = 60;

  worksheet.columns.forEach((column) => {
    column.alignment = {
      // wrapText: true,
      vertical: "middle",
      horizontal: "center",
    };
    column.width = 20;
  });
  // IMPORTANT: override header alignment AFTER you center all columns
  // Column A: table column (Sr.No) centered; meta labels A3–A5 stay left via per-cell above
  worksheet.getColumn("A").alignment = {
    horizontal: "center",
    vertical: "middle",
  };
  worksheet.getColumn("B").alignment = { horizontal: "left", vertical: "middle" };

  // Also force the exact cells (extra safe)
  ["A2", "A3", "A4", "A5", "B2", "B3", "B4", "B5"].forEach((addr) => {
    worksheet.getCell(addr).alignment = { horizontal: "left", vertical: "middle" };
  });


  let BlockedInvs = await listBlockedInventories();

  let totalOfRPM = 0;
  let totalOfSR = 0;
  let totalOfPC = 0;
  let totalOfMC = 0;
  let totalOfInnovation = 0;

  proposalInventories.forEach((inventory, index) => {
    let isInventoryBlocked = false;
    if (BlockedInvs.length > 0) {
      if (BlockedInvs.includes(inventory?.invId?._id)) {
        isInventoryBlocked = true;
      }
    }

    console.log("from excel blocked inv", isInventoryBlocked);

    if (Number(inventory?.invId?.ratePerMonth)) {
      totalOfRPM += inventory?.invId?.ratePerMonth;
    }
    if (Number(inventory?.specialRate)) {
      totalOfSR += inventory?.specialRate;
    }
    if (Number(inventory?.mountingCharge)) {
      totalOfMC += inventory?.mountingCharge;
    }
    if (Number(inventory?.printingCharge)) {
      totalOfPC += inventory?.printingCharge;
    }
    if (Number(inventory?.innovationRate)) {
      totalOfInnovation += inventory?.innovationRate;
    }

    // Create hyperlink for LAT LONG column
    const latLongHyperlink = `https://www.google.com/maps?q=${inventory?.invId?.lat},${inventory?.invId?.lng}`;

    let availableText = getProposalAvailableFromLabel(inventory?.invId);
    if (isInventoryBlocked && availableText !== "-") {
      availableText += " / BLOCKED";
    }

    let row;
    if (packageRate) {
      row = [
        index + 1,
        inventory?.invId.locations?.name || "-", // Removed Area, kept only Location
        inventory?.invId?.typeOfMedia?.name || "-",
        asExcelNumber(inventory?.invId?.width),
        asExcelNumber(inventory?.invId?.height),
        ...(isAllKiosk
          ? [asExcelNumber(inventory?.invId?.unit)]
          : []),
        asSqFt(inventory, isAllKiosk),
        inventory?.invId?.Illu ?? "-",
        ratePerMonthNumeric(inventory, isAllKiosk),
        packageRate ?? "-",
        0,
        availableText,
        inventory?.invId?.lat || "-",
        // inventory?.invId?.lng || "-",
      ];
    } else {
      if (!isThereInnovationRate) {
        if (hasSpecialRateChanged) {
          row = [
            index + 1,
            inventory?.invId.locations?.name || "-",
            inventory?.invId?.typeOfMedia?.name || "-",
            asExcelNumber(inventory?.invId?.width),
            asExcelNumber(inventory?.invId?.height),
            ...(isAllKiosk
              ? [asExcelNumber(inventory?.invId?.unit)]
              : []),
            asSqFt(inventory, isAllKiosk),
            inventory?.invId?.Illu ?? "-",
            ratePerMonthNumeric(inventory, isAllKiosk),
            asExcelNumber(inventory.specialRate),
            asExcelNumber(inventory.printingCharge),
            asExcelNumber(inventory.mountingCharge),
            availableText,
            inventory?.invId?.lat || "-",
            // inventory?.invId?.lng || "-",
          ];
        } else {
          row = [
            index + 1,
            inventory?.invId.locations?.name || "-",
            inventory?.invId?.typeOfMedia?.name || "-",
            asExcelNumber(inventory?.invId?.width),
            asExcelNumber(inventory?.invId?.height),
            ...(isAllKiosk
              ? [asExcelNumber(inventory?.invId?.unit)]
              : []),
            asSqFt(inventory, isAllKiosk),
            inventory?.invId?.Illu ?? "-",
            ratePerMonthNumeric(inventory, isAllKiosk),
            asExcelNumber(inventory.printingCharge),
            asExcelNumber(inventory.mountingCharge),
            availableText,
            inventory?.invId?.lat || "-",
            // inventory?.invId?.lng || "-",
          ];
        }
      } else {
        if (hasSpecialRateChanged) {
          row = [
            index + 1,
            inventory?.invId.locations?.name || "-",
            inventory?.invId?.typeOfMedia?.name || "-",
            asExcelNumber(inventory?.invId?.width),
            asExcelNumber(inventory?.invId?.height),
            ...(isAllKiosk
              ? [asExcelNumber(inventory?.invId?.unit)]
              : []),
            asSqFt(inventory, isAllKiosk),
            inventory?.invId?.Illu ?? "-",
            ratePerMonthNumeric(inventory, isAllKiosk),
            asExcelNumber(inventory.specialRate),
            asExcelNumber(inventory.printingCharge),
            asExcelNumber(inventory.mountingCharge),
            asExcelNumber(inventory.innovationRate),
            availableText,
            inventory?.invId?.lat || "-",
            // inventory?.invId?.lng || "-",
          ];
        } else {
          row = [
            index + 1,
            inventory?.invId.locations?.name || "-",
            inventory?.invId?.typeOfMedia?.name || "-",
            asExcelNumber(inventory?.invId?.width),
            asExcelNumber(inventory?.invId?.height),
            ...(isAllKiosk
              ? [asExcelNumber(inventory?.invId?.unit)]
              : []),
            asSqFt(inventory, isAllKiosk),
            inventory?.invId?.Illu ?? "-",
            ratePerMonthNumeric(inventory, isAllKiosk),
            asExcelNumber(inventory.printingCharge),
            asExcelNumber(inventory.mountingCharge),
            asExcelNumber(inventory.innovationRate),
            availableText,
            inventory?.invId?.lat || "-",
            // inventory?.invId?.lng || "-",
          ];
        }
      }
    }
    // Keep Sr.no, W, H, Unit, Sq Ft as Excel numbers; format other numeric cols as Indian strings where needed
    row = row.map((val) =>
      typeof val === "number" && Number.isFinite(val)
        ? val
        : formatIndianAllNumbers(val)
    );
    const newRow = worksheet.addRow(row);
    applyCommaNumFmtToDataRow(newRow, displayCostHeader);

    // Apply borders to each cell in the newly added row
    row.forEach((cellValue, cellIndex) => {

      newRow.getCell(cellIndex + 1).border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add hyperlink to LAT LONG column
    const latLongIndex = displayCostHeader.findIndex(h => h.includes("Lat Long")) + 1;
    if (latLongIndex > 0) {
      const latLongCell = newRow.getCell(latLongIndex);
      latLongCell.value = {
        text: `View on Map`,
        hyperlink: latLongHyperlink,
      };
      latLongCell.alignment = {
        wrapText: true,
        vertical: "middle",
        horizontal: "center",
      };
      latLongCell.font = {
        color: { argb: "0000FF" },
        underline: true,
      };
    }

    const availableFromIndex = displayCostHeader.indexOf("Available From") + 1;
    if (availableFromIndex > 0 && isInventoryBlocked) {
      newRow.getCell(availableFromIndex).font = {
        color: { argb: "FF0000" },
      };
    }
  });

  let displayCostHeader2 = [];
  for (let i of displayCostHeader) {
    displayCostHeader2.push("");
  }

  // Total row
  let totalRow;
  if (packageRate) {
    totalRow = [
      "",
      "Total",
      "",
      "",
      "",
      ...(isAllKiosk ? [""] : []),
      "",
      "",
      asExcelNumber(totalOfRPM),
      "",
      0,
      "",
      "",
      // "",
    ];
  } else {
    if (!isThereInnovationRate) {
      if (hasSpecialRateChanged) {
        totalRow = [
          "",
          "Total",
          "",
          "",
          "",
          ...(isAllKiosk ? [""] : []),
          "",
          "",
          asExcelNumber(totalOfRPM),
          totalOfSR,
          asExcelNumber(totalOfPC),
          asExcelNumber(totalOfMC),
          "",
          "",
          // "",
        ];
      } else {
        totalRow = [
          "",
          "Total",
          "",
          "",
          "",
          ...(isAllKiosk ? [""] : []),
          "",
          "",
          asExcelNumber(totalOfRPM),
          asExcelNumber(totalOfPC),
          asExcelNumber(totalOfMC),
          "",
          "",
          // "",
        ];
      }
    } else {
      if (hasSpecialRateChanged) {
        totalRow = [
          "",
          "Total",
          "",
          "",
          "",
          ...(isAllKiosk ? [""] : []),
          "",
          "",
          asExcelNumber(totalOfRPM),
          totalOfSR,
          asExcelNumber(totalOfPC),
          asExcelNumber(totalOfMC),
          totalOfInnovation,
          "",
          "",
          // "",
        ];
      } else {
        totalRow = [
          "",
          "Total",
          "",
          "",
          "",
          ...(isAllKiosk ? [""] : []),
          "",
          "",
          asExcelNumber(totalOfRPM),
          asExcelNumber(totalOfPC),
          asExcelNumber(totalOfMC),
          totalOfInnovation,
          "",
          "",
          // "",
        ];
      }
    }
  }
  totalRow = totalRow.map((val) =>
    typeof val === "number" && Number.isFinite(val)
      ? val
      : formatIndianAllNumbers(val)
  );
  const anotherRow = worksheet.addRow(totalRow);
  applyCommaNumFmtToDataRow(anotherRow, displayCostHeader);

  // Adjust merge cells to start from column B (Location) instead of C
  worksheet.mergeCells(
    `B${proposalInventories.length + 8}:G${proposalInventories.length + 8}`
  );
  worksheet.getCell(`B${proposalInventories.length + 8}`).font = {
    bold: true,
  };
  worksheet.getCell(`B${proposalInventories.length + 8}`).alignment = {
    vertical: "middle",
    horizontal: "center",
  };

  displayCostHeader2.forEach((header, index) => {
    worksheet
      .getRow(proposalInventories.length + 8)
      .getCell(index + 1).border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Merge the 'Rate Per Month' column cells if packageRate exists
  if (packageRate) {
    const startRow = 8;
    const endRow = proposalInventories.length + 7;
    const ratePerMonthCol = displayCostHeader.indexOf("Rate Per Month") + 1;
    const colLetter = String.fromCharCode(64 + ratePerMonthCol);
    worksheet.mergeCells(`${colLetter}${startRow}:${colLetter}${endRow}`);
    worksheet.getCell(`${colLetter}${startRow}`).font = { bold: true };
    worksheet.getCell(`${colLetter}${startRow}`).alignment = {
      vertical: "middle",
      horizontal: "center",
    };
  }

  worksheet.addRow(displayCostHeader2).font = { bold: true };
  displayCostHeader2.forEach((header, index) => {
    worksheet.getRow(worksheet.lastRow.number - 1).getCell(index + 1).border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    worksheet.getRow(worksheet.lastRow.number - 1).getCell(index + 1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFF00" },
    };
  });

  worksheet.addRow([""]);
  let currentRow = worksheet.lastRow ? worksheet.lastRow.number + 1 : 1;

  // Add rows and merge cells for notes
  const rowsToMerge = [
    "* Sites are subject to availability at the time of your confirmation.",
    "* Site will be block only for one day.",
    "* Mail Conﬁrmation or Purchase Order is mandatory to start the campaign.",
    "* GST (18%) will be charged as per government rules and regulations.",
    "* For any reasons, if your flex is damaged, it will be your responsibility to provide us with a new flex.",
  ];

  rowsToMerge.forEach((text, index) => {
    worksheet.addRow([""]);
    worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
    const row = worksheet.getRow(currentRow);
    row.getCell(1).value = text;
    row.getCell(1).font = { bold: true };
    row.getCell(1).alignment = { vertical: "middle", horizontal: "left" };
    currentRow++;
  });

  // worksheet.addRow([""]);
  // worksheet.addRow(["Thanks"]);
  // worksheet.addRow(["&"]);
  // worksheet.addRow(["Regards"]);
  // worksheet.addRow([""]);
  // worksheet.addRow(["YELLOW"]);
  // worksheet.addRow(["SPOT"]);
  // worksheet.addRow(["OOH"]).font = {
  //   bold: true,
  // };
  worksheet.addRow([""]);

  // Footer: merge across table width so text isn't clipped (column A is narrow)
  const footerLastCol = getExcelColumnLetter(displayCostHeader.length);

  const addFooterLine = (text) => {
    worksheet.addRow([text]);
    const r = worksheet.lastRow.number;
    worksheet.mergeCells(`A${r}:${footerLastCol}${r}`);
    const row = worksheet.getRow(r);
    row.getCell(1).value = text;
    row.getCell(1).font = { bold: true };
    row.getCell(1).alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true,
    };
  };
  addFooterLine("Thanks & Regards");
  addFooterLine("YELLOW SPOT OOH");



  // Column widths
  const mediumWidth = 15;
  const largeWidth = ["B"];
  const cWidth = ["C"];
  const specificWidth = ["D", "E"];
  const fWidth = ["F"];
  const gWidth = ["G"];
  const widthForLatLong = ["M", "N"];
  const hijWidth = ["H", "I", "J"];
  const kWidth = ["K"];


  worksheet.columns.forEach((column) => {
    if (largeWidth.includes(column.letter)) {
      column.width = 45;
    } else if (cWidth.includes(column.letter)) {
      column.width = 9;
    } else if (specificWidth.includes(column.letter)) {
      column.width = 8;
    } else if (fWidth.includes(column.letter)) {
      column.width = 6;
    } else if (gWidth.includes(column.letter)) {
      column.width = 5;
    } else if (widthForLatLong.includes(column.letter)) {
      column.width = 18;
    } else if (hijWidth.includes(column.letter)) {
      column.width = 10;
    } else if (kWidth.includes(column.letter)) {
      column.width = 14;
    } else {
      column.width = mediumWidth;
    }
  });
  worksheet.getColumn("A").width = 6;

  // Keep header values readable
  worksheet.getColumn("B").width = 60;


  const rowNumber = 7;
  const desiredHeight = 50;
  worksheet.getRow(rowNumber).height = desiredHeight;
  worksheet.getRow(rowNumber).eachCell({ includeEmpty: true }, (cell) => {
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  if (!isEmail) {
    saveAs(
      blob,
      `${clientName}_Proposal_${moment().format("YYYY-MM-DD")}.xlsx`
    );
  }

  if (isEmail) {
    const fileName = `${clientName}_Proposal_${moment().format(
      "YYYY-MM-DD"
    )}.xlsx`;
    const requestBody = {
      fileName,
      mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };
    const token = window.localStorage.getItem("token");
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/uploadPolicyForEmails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`);
        return;
      }

      const data = await response.json();

      const formData = new FormData();
      const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      // Only use fields from the policy - do not add any extra fields (e.g. content-type)
      // or S3 returns "Invalid according to Policy: Extra input fields"
      for (const [key, value] of Object.entries(data.data.fields)) {
        if (key.toLowerCase() === "content-type") {
          formData.set(key, contentType);
        } else {
          formData.set(key, value);
        }
      }

      const file = new File([blob], fileName, {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      formData.append("file", file);
      const finalurl = convertS3ToCloudFront(
        `${data.data.url}/${data.filePath}`,
      );

      console.log("finalurl : ", finalurl);

      try {
        const almostFinalResponse = await fetch(data.data.url, {
          method: "POST",
          body: formData,
        });

        if (almostFinalResponse.ok) {
          console.log("File uploaded successfully.");
        } else {
          console.log(
            "Error occurred:",
            almostFinalResponse.status,
            almostFinalResponse.statusText
          );
        }
      } catch (error) {
        console.log("Error occurred:", error);
      }

      setExcelURL(finalurl);
    } catch (error) {
      console.error("Error fetching upload policy:", error);
    }
  }

  setShowLoader(false);
};

export default handleDownloadExcel;